import GlobalDataContext from "@components/Layout/GlobalDataContext"
import { MenuItemTogglableCompactProps } from "@components/MenuItemTogglableCompact"
import ProductFilter from "@components/ProductFilterDesktop"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import useScrollHandler from "@hooks/useScrollHandler"
import { getIn } from "@utils/index"
import { FilterFunnelProps, MenuDataProps } from "@utils/types"
import React from "react"
import styled from "styled-components"

import BrandFilter from "./BrandFilter"
import Breadcrumbs from "./Breadcrumbs"
import DeviceFilter from "./DeviceFilter"
import DeviceTypeFilter from "./DeviceTypeFilter"

const Container = styled.div`
  width: calc(2 / 12 * 100vw);
  height: 100%;
  min-width: 300px;
`

const StickyWrapper = styled.div<{
  promoBannerHeight: number
  shouldShorten: boolean
}>`
  position: fixed;
  position: sticky;
  top: ${({ promoBannerHeight }) => `calc(${promoBannerHeight}px + 88px)`};
  width: calc(2 / 12 * 100vw);
  height: ${({ promoBannerHeight }) =>
    `calc(100vh - ${promoBannerHeight}px - 88px)`};
  min-width: 300px;
  padding-left: calc(0.5 / 12 * 100vw);
  padding-bottom: 56px;
  transition: transform ${animation.quick} ${cubicBezier};
  ${({ shouldShorten }) =>
    shouldShorten ? "transform: translateY(-24px);" : ""}
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.dividerLineGray};
`

interface SideNavProps {
  activeBrands: number
  activeColors: number
  activeDiscountTiers: number
  activeProductSeries: number
  activeSubTypes: number
  brands: Array<any>
  colors: Array<any>
  devices: Array<any>
  discountTiers: Array<any>
  displayBanner: boolean
  filterFunnel: FilterFunnelProps
  globalData: any
  hasBundleProducts: boolean
  hasMagnetCompatibleProducts: boolean
  hasPrintedDesigns: boolean
  isClearance: boolean
  isCustomPrints: boolean
  menuData: MenuDataProps
  onResetFilters: (event) => void
  onToggleBundleProducts: () => void
  onToggleClearance: (event) => void
  onToggleInStock: (event) => void
  onToggleMagnetCompatibleProducts: () => void
  onTogglePrintedDesigns: () => void
  productSeries: Array<any>
  showBundleProducts: boolean
  showClearance: boolean
  showInStock: boolean
  showMagnetCompatibleProducts: boolean
  showPrintedDesigns: boolean
  subTypes: Array<MenuItemTogglableCompactProps>
}

const SideNav: React.FC<SideNavProps> = ({
  activeBrands,
  activeColors,
  activeDiscountTiers,
  activeProductSeries,
  activeSubTypes,
  brands,
  colors,
  devices,
  discountTiers,
  displayBanner,
  filterFunnel,
  globalData,
  hasBundleProducts,
  hasMagnetCompatibleProducts,
  hasPrintedDesigns,
  isClearance,
  isCustomPrints,
  menuData,
  onResetFilters,
  onToggleBundleProducts,
  onToggleClearance,
  onToggleInStock,
  onToggleMagnetCompatibleProducts,
  onTogglePrintedDesigns,
  productSeries,
  showBundleProducts,
  showClearance,
  showInStock,
  showMagnetCompatibleProducts,
  showPrintedDesigns,
  subTypes,
}) => {
  const { showPromoBanner }: any = React.useContext(GlobalDataContext)

  const [shortenHeader, setShortenHeader] = React.useState(false)
  const handleShortenHeight = () => {
    const html = document.getElementsByTagName("html")
    if (html && html[0] && html[0].getBoundingClientRect().top < -40) {
      setShortenHeader(true)
    } else {
      setShortenHeader(false)
    }
  }

  useScrollHandler({ onScroll: handleShortenHeight })

  const filters = getIn(menuData, "filters", {})

  const renderFilters = () => {
    const { brand, collection, device, deviceType, series } = filters

    if (
      isCustomPrints ||
      isClearance ||
      collection ||
      series ||
      (deviceType && brand && device)
    ) {
      return (
        <ProductFilter
          activeBrands={activeBrands}
          activeColors={activeColors}
          activeDiscountTiers={activeDiscountTiers}
          activeProductSeries={activeProductSeries}
          activeSubTypes={activeSubTypes}
          brands={brands}
          colors={colors}
          devices={devices}
          discountTiers={discountTiers}
          hasBundleProducts={hasBundleProducts}
          hasMagnetCompatibleProducts={hasMagnetCompatibleProducts}
          hasPrintedDesigns={hasPrintedDesigns}
          isClearance={isClearance}
          isCustomPrints={isCustomPrints}
          onResetFilters={onResetFilters}
          onToggleBundleProducts={onToggleBundleProducts}
          onToggleClearance={onToggleClearance}
          onToggleInStock={onToggleInStock}
          onToggleMagnetCompatibleProducts={onToggleMagnetCompatibleProducts}
          onTogglePrintedDesigns={onTogglePrintedDesigns}
          productSeries={!series ? productSeries : []}
          showBundleProducts={showBundleProducts}
          showClearance={showClearance}
          showInStock={showInStock}
          showMagnetCompatibleProducts={showMagnetCompatibleProducts}
          showPrintedDesigns={showPrintedDesigns}
          subTypes={subTypes}
        />
      )
    } else if (deviceType && brand) {
      return (
        <DeviceFilter
          brandSlug={brand.slug}
          data={menuData || {}}
          deviceTypeSlug={deviceType.slug}
        />
      )
    } else if (deviceType) {
      return (
        <BrandFilter
          data={menuData || {}}
          deviceTypeSlug={deviceType.slug}
          filterFunnel={filterFunnel}
        />
      )
    } else {
      return (
        <DeviceTypeFilter
          data={getIn(globalData, "menuItems", [])}
          filterFunnel={filterFunnel}
        />
      )
    }
  }

  const previousBreadcrumb = isClearance
    ? null
    : getIn(menuData, "filters.previous")
  const currentBreadcrumb = isClearance
    ? filters.deviceType
      ? `Shop ${filters.deviceType.name} Sale`
      : "Shop Sale"
    : getIn(menuData, "filters.current")

  return (
    <Container>
      <StickyWrapper
        promoBannerHeight={showPromoBanner ? 36 : 0}
        shouldShorten={shortenHeader}
      >
        <Breadcrumbs
          current={currentBreadcrumb}
          displayBanner={displayBanner}
          previous={previousBreadcrumb}
        />
        <Divider />
        {renderFilters()}
      </StickyWrapper>
    </Container>
  )
}

export default SideNav
